import { useEffect, useRef, useState } from "react";
import { AppHeader } from "./components/AppHeader";
import { AssistantPanel } from "./components/AssistantPanel";
import { BillingPage } from "./components/BillingPage";
import { ImagePlan } from "./components/ImagePlan";
import { ImageReview } from "./components/ImageReview";
import { LoginPage, LogoutPage, PricingPage } from "./components/AccountPages";
import { ConnectionsPage } from "./components/ConnectionsPage";
import { ListingsPage } from "./components/ListingsPage";
import { ProductSetup } from "./components/ProductSetup";
import { SaasNavigation } from "./components/SaasNavigation";
import { StageRail } from "./components/StageRail";
import { useSaasDemo } from "./hooks/useSaasDemo";
import { useWorkspace } from "./hooks/useWorkspace";
import { useTheme } from "./hooks/useTheme";
import type { AppView } from "./types/workspace";
import "./components/Polish.css";
import "./components/HeaderI18n.css";
import "./components/SaasShell.css";

export default function App() {
  const saas = useSaasDemo();
  const workspace = useWorkspace(Boolean(saas.state.session));
  const { state } = workspace;
  const theme = useTheme();
  const [view, setView] = useState<AppView>("workspace");
  const accountedVisuals = useRef(0);
  useEffect(() => {
    if (state.images.some((image) => image.status === "generating")) return;
    const completedVisuals = state.images.reduce(
      (total, image) => total + image.version,
      0,
    );
    if (completedVisuals > accountedVisuals.current)
      saas.consumeVisualCredits(completedVisuals - accountedVisuals.current);
    accountedVisuals.current = completedVisuals;
  }, [saas, state.images]);
  const authenticate = async (identifier: string, password: string) => {
    await saas.startSession(identifier, password);
    setView("workspace");
  };
  const signOut = () => {
    saas.signOut();
    setView("logout");
  };
  const navigate = (next: AppView) => {
    if (
      ["billing", "listings", "connections"].includes(next) &&
      !saas.state.session
    )
      setView("login");
    else setView(next);
  };
  const approvedImageCount = state.images.filter(
    (image) => image.approved,
  ).length;
  function renderContent() {
    switch (view) {
      case "workspace":
        return (
          <div className="workspace-grid">
            <StageRail
              stage={state.stage}
              productReady={state.productReady}
              onStageChange={workspace.setStage}
              locale={state.locale}
            />
            <AssistantPanel workspace={workspace} />
            {state.stage === "setup" && <ProductSetup workspace={workspace} />}
            {state.stage === "plan" && <ImagePlan workspace={workspace} />}
            {state.stage === "review" && <ImageReview workspace={workspace} />}
          </div>
        );
      case "billing":
        return (
          <BillingPage
            locale={state.locale}
            state={saas.state}
            onUpdate={saas.updateBilling}
          />
        );
      case "connections":
        return (
          <ConnectionsPage
            locale={state.locale}
            state={saas.state}
            onConnect={saas.connectAmazonJapan}
          />
        );
      case "listings":
        return (
          <ListingsPage
            locale={state.locale}
            state={saas.state}
            approvedImageCount={approvedImageCount}
            onUpdate={saas.updateDraft}
            onPublish={saas.requestPublication}
            onConfirm={saas.confirmDirectPublication}
            onCancel={saas.cancelDirectPublication}
          />
        );
      case "pricing":
        return (
          <div className="account-shell">
            <PricingPage
              locale={state.locale}
              onChoose={() => setView("login")}
            />
          </div>
        );
      case "logout":
        return (
          <div className="account-shell">
            <LogoutPage
              locale={state.locale}
              onReturn={() => setView("workspace")}
            />
          </div>
        );
      case "login":
        return (
          <div className="account-shell">
            <LoginPage locale={state.locale} onContinue={authenticate} />
          </div>
        );
    }
  }

  const content = renderContent();
  return (
    <div
      className={`studio-page ${state.locale === "en" ? "locale-en" : ""}`}
      lang={state.locale}
    >
      <main className="workspace-shell">
        <AppHeader
          view={view}
          locale={state.locale}
          theme={theme.theme}
          onThemeToggle={theme.toggleTheme}
          signedIn={Boolean(saas.state.session)}
          onLocaleChange={workspace.setLocale}
          onNavigate={navigate}
          onSignOut={signOut}
        />
        {saas.state.session && !["pricing", "login"].includes(view) ? (
          <div className="saas-layout">
            <SaasNavigation
              locale={state.locale}
              view={view}
              onNavigate={navigate}
            />
            {content}
          </div>
        ) : (
          content
        )}
      </main>
    </div>
  );
}
